import { StockStatus } from '@constants/vendorVariantConstants';
import { DesignAwardPlace } from '@components/shared/DesignAwards/DesignAwardBadge';
import { VendorTieredDiscountCodeModel } from '@codegen-basetypes';
import { IVendorVariantReview } from './i-vendor-variant-review';

export type AvailabilityStatus = 'in_stock' | 'back_ordered' | 'out_of_stock' | 'unavailable_add_to_cart' | 'unavailable';

export interface IVendorVariantReviews {
  items: IVendorVariantReview[];
}

export interface IVendorVariantReviewStats
{
  average: number;
  count: number;
}

export interface ICode
{
  id: number;
  name: string;
  discount: number;
  discountType: number;
  active: boolean;
  minimumCartTotal?: number;
  code?: string;
}

export interface IVendorVariantInternal {
  id: number;
  created?: Date;
  modified?: Date;
  brandId?: number;
  creatorId?: number;
  description?: string;
  havenlyVerified?: boolean;
  isAssemblyRequired?: boolean;
  isSyncable?: boolean;
  manualTaxonomyId?: number;
  predictedTaxonomyId?: number;
  shippingTypeId?: number;
  size?: string;
  subSellerId?: number;
  taxonomyId?: number;
  vendorId?: number;
  isOnSale?: boolean;
  vendorVariantGroupId?: number;
  weblink?: string;
  weight?: string;

  attributeValues: IProductAttribute[];

  price: string;
  title: string;
  currentPrice?: number;
  retailPrice?: IPrice;
  salePrice?: IPrice;
  saleOnSalePrice?: {price: number; percentage: number};
  discountedPrice?: number;
  availability: AvailabilityStatus;
  havenlyUrl: string;

  isReturnable: boolean;

  userOpinion?: {
    opinion?: number;
  };

  vendor: {
    id: string;
    name: string;
    publicBrandUrl:string;
  };

  etaText?: string;
  feedCreatorId?: number;

  vendorPromotion?: IVendorPromotion;

  vendorTieredDiscountCode?: VendorTieredDiscountCodeModel;

  dimensions: string;
  material: string;
  color: string;

  mainVendorVariantMediaId: number;

  image: IVendorVariantMedia;
  images: IVendorVariantMedia[];

  blogPosts: IBlogPostCardProps[];

  reviewStats: IVendorVariantReviewStats;
  reviews?: IVendorVariantReviews;

  returnPolicy: IProductReturnPolicy;
  shippingPolicy?: IProductShippingPolicy;
  shippingType: IProductShippingType;

  taxonomy: {
    title: string;
    preferredTaxonomyCategory?: ITaxonomyCategory;
  };

  sku: string;
  productCanonicalUrl: string;

  designAward: DesignAwardPlace;

  visibilities: number[];

  merchGroupTaxonomy?: IMerchGroupCategory;
  vendorProductGroup?: IVendorProductGroup;
}

export interface IMerchGroupCategory {
  id: string;
  slug: string;
  title: string;
  parentMerchGroupCategory?: IMerchGroupCategory | null;
}

export interface IPrice {
  price?: number | null;
}

export interface IAttributeType {
  id: string;
  title: string;
}

export interface IProductAttribute {
  attributeType: IAttributeType;
  value: string;
}

export interface IProductReturnPolicy {
  description?: string;
  url?: string;
}

export interface IProductShippingPolicy {
  description?: string;
  url: string;
  urlText: string;
}

export interface IProductShippingType {
  title: string;
}

export interface IBlogPostCardProps {
  title: string;
  imageUrl: string;
  url: string;
}

export interface IVendorVariantMedia {
  id: string;
  uri: string;
  height?: number;
  width?: number;
}

export interface IVendorPromotion {
  promoText?: string;
  shipping: boolean;
  terms?: string;
}

export interface IVendorProductGroup {
  id: string;
  collectionName: string;
  vendorProductGroupName: string;
}

export interface IVendorVariant {
  vendorVariantById: IVendorVariantInternal;
}

export interface IVendorVariantConfig {
  vendorVariantByGroupAndOptionsV2: IVendorVariantInternal;
}

export enum IVendorVariantVisibilityType {
  VISIBLE_IN_SHOP = 1,
  VISIBLE_IN_AIRBNB = 2,
  VISIBLE_IN_BOARD_BUILDER = 3,
}

export interface ITaxonomyCategory {
  id: string;
  title: string;
  slug: string;
  parentTaxonomyCategory?: ITaxonomyCategory;
}

export enum TopLevelTaxonomyCategoryTitles {
  FURNITURE = 'Furniture',
  LIGHTING = 'Lighting',
  RUGS = 'Rugs',
  ART_MIRRORS = 'Art & Mirrors',
  DECOR_PILLOWS = 'Decor & Pillows',
  BEDDING = 'Bedding'
}

export function isInStock(availability: AvailabilityStatus|string|undefined|null) {
  if (!availability) {
    return false;
  }

  return availability === StockStatus.IN_STOCK || availability === StockStatus.BACK_ORDERED;
}

export function isBackordered(availability: AvailabilityStatus|string|undefined|null) {
  if (!availability) {
    return false;
  }

  return availability === StockStatus.BACK_ORDERED;
}

export function isUnavailable(availability: AvailabilityStatus|string|undefined|null) {
  if (!availability) {
    return false;
  }

  return [
    'unavailable',
    'unavailable_add_to_cart',
  ].includes(availability);
}
